// ============================================
// FILE: src/components/features/Search/SearchFormComponent.js
// Reusable search form component for both AuthForm and SearchPage
// ============================================
import React, { useState } from 'react';
import { Search, MapPin } from 'lucide-react';
import LocationPicker from './LocationPicker';

// Dog breeds list
const DOG_BREEDS = [
  'Unknown',
  'Labrador Retriever',
  'Golden Retriever',
  'German Shepherd',
  'French Bulldog',
  'Bulldog',
  'Poodle',
  'Beagle',
  'Dachshund',
  'Siberian Husky',
  'Australian Shepherd',
  'Rottweiler',
  'Boxer',
  'Great Dane',
  'Chihuahua',
  'Shih Tzu',
  'Mixed Breed',
  'Other'
];

// Category filter options with descriptions
const CATEGORY_OPTIONS = [
  {
    value: 'all',
    label: 'All Posts',
    description: 'Search for all pets across missing, found, and adoption posts'
  },
  {
    value: 'missing',
    label: 'Missing Pets',
    description: 'Search only for pets that were reported missing by owners'
  },
  {
    value: 'found',
    label: 'Found Pets',
    description: 'Search only for pets that were reported as found by users'
  }
];

const SearchFormComponent = ({
  onSearch,
  isLoading,
  showCategoryFilter = false
}) => {
  const [searchParams, setSearchParams] = useState({
    pet_name: '',
    animal_type: '',
    breed: 'Unknown',
    sex: 'all',
    description: '',
    location: '',
    latitude: null,
    longitude: null,
    radius_km: 50,
    category: 'all' // NEW: Category filter
  });

  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [errors, setErrors] = useState({});

  // Proper location state management
  const handleLocationSelected = (loc) => {
    console.log('Location selected:', loc);

    // Validate USA location for zip code
    if (loc.name && loc.name.match(/\d{5}/)) {
      // If zip code format is detected, verify coordinates are USA
      if (loc.latitude < 24 || loc.latitude > 50 || loc.longitude > -66 || loc.longitude < -125) {
        alert('Only USA locations are supported for zip code searches. Please select a location within the USA.');
        return;
      }
    }

    setSearchParams({
      ...searchParams,
      location: loc.name,
      latitude: loc.latitude,
      longitude: loc.longitude
    });
    setErrors({ ...errors, location: '' });
    setShowLocationPicker(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validation
    if (!searchParams.animal_type) {
      newErrors.animal_type = 'Animal type is required';
    }
    // Check for valid coordinates
    if (searchParams.latitude === null || searchParams.longitude === null) {
      newErrors.location = 'Location is required - please select from map';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSearch(searchParams);
  };

  const handleAnimalTypeChange = (e) => {
    const type = e.target.value;
    setSearchParams({
      ...searchParams,
      animal_type: type,
      breed: type === 'dog' ? 'Unknown' : ''
    });
    setErrors({ ...errors, animal_type: '' });
  };

  return (
    <>
      <form onSubmit={handleSearch} className="space-y-6">
        {/* Row 1: Pet Name Search */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Pet Name (Optional)
          </label>
          <input
            type="text"
            value={searchParams.pet_name}
            onChange={(e) => setSearchParams({ ...searchParams, pet_name: e.target.value })}
            placeholder="e.g., Bella, Max, Fluffy, Rocky"
            className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base"
          />
          <p className="text-xs text-gray-500 mt-1">Search for pets by their name</p>
        </div>

        {/* Row 2: Category Filter (Only show when showCategoryFilter is true) */}
        {showCategoryFilter && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Post Category
            </label>
            <div className="flex flex-wrap gap-6">
              {CATEGORY_OPTIONS.map(option => (
                <div key={option.value} className="group relative flex items-start">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="category"
                      value={option.value}
                      checked={searchParams.category === option.value}
                      onChange={(e) => setSearchParams({ ...searchParams, category: e.target.value })}
                      className="w-4 h-4 cursor-pointer mt-1 flex-shrink-0"
                    />
                    <div>
                      <span className="text-sm md:text-base text-gray-700 font-medium">
                        {option.label}
                      </span>
                      {/* Mobile visible description */}
                      <p className="text-xs text-gray-500 md:hidden mt-1">
                        {option.description}
                      </p>
                    </div>
                  </label>

                  {/* Hover Tooltip */}
                  <div className="absolute bottom-full mb-2 left-0 hidden group-hover:block z-10">
                    <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap">
                      {option.description}
                      <div className="absolute top-full left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Row 3: Animal Type & Breed */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Animal Type <span className="text-red-500">*</span>
            </label>
            <select
              value={searchParams.animal_type}
              onChange={handleAnimalTypeChange}
              className={`w-full px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base ${
                errors.animal_type ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Animal Type</option>
              <option value="dog">🐕 Dog</option>
              <option value="cat">🐈 Cat</option>
              <option value="bird">🦜 Bird</option>
              <option value="rabbit">🐰 Rabbit</option>
              <option value="other">🐾 Other</option>
            </select>
            {errors.animal_type && (
              <p className="text-red-500 text-xs mt-1">{errors.animal_type}</p>
            )}
          </div>

          {/* Breed - Only visible for dogs */}
          {searchParams.animal_type === 'dog' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Breed (Optional)
              </label>
              <select
                value={searchParams.breed}
                onChange={(e) => setSearchParams({ ...searchParams, breed: e.target.value })}
                className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base"
              >
                {DOG_BREEDS.map(breed => (
                  <option key={breed} value={breed}>
                    {breed}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Row 4: Sex */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Sex
          </label>
          <div className="flex flex-wrap gap-4">
            {[
              { value: 'all', label: 'All' },
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
              { value: 'unknown', label: 'Unknown' }
            ].map(option => (
              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="sex"
                  value={option.value}
                  checked={searchParams.sex === option.value}
                  onChange={(e) => setSearchParams({ ...searchParams, sex: e.target.value })}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className="text-sm md:text-base text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Row 5: Description */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Description (Optional)
          </label>
          <textarea
            value={searchParams.description}
            onChange={(e) => setSearchParams({ ...searchParams, description: e.target.value })}
            placeholder="Describe the pet you're looking for... e.g., Golden retriever with white chest, friendly, wearing blue collar"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm md:text-base"
            rows={4}
          />
        </div>

        {/* Row 6: Location Picker */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 md:p-6">
          <label className="block text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <MapPin size={18} className="text-blue-600" />
            Location (City, State, or Zip Code - USA Only) <span className="text-red-500">*</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2">
              {/* Selected location display */}
              <div className="w-full px-4 py-2 md:py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm md:text-base">
                {searchParams.location ? (
                  <p className="text-gray-700 font-medium">{searchParams.location}</p>
                ) : (
                  <p className="text-gray-500">Click 'Pick on Map' to select a location</p>
                )}
              </div>
              {searchParams.latitude && (
                <p className="text-xs text-blue-600 mt-2">
                  📍 Coordinates: {searchParams.latitude.toFixed(4)}, {searchParams.longitude.toFixed(4)}
                </p>
              )}
              {errors.location && (
                <p className="text-red-500 text-xs mt-1">{errors.location}</p>
              )}
            </div>

            {/* Button to open location picker */}
            <button
              type="button"
              onClick={() => setShowLocationPicker(true)}
              className="px-4 py-2 md:py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold text-sm md:text-base"
            >
              {searchParams.latitude ? '🗺️ Change' : '🗺️ Pick on Map'}
            </button>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Search Radius: <span className="text-blue-600">{searchParams.radius_km} km</span>
            </label>
            <input
              type="range"
              min="5"
              max="200"
              step="5"
              value={searchParams.radius_km}
              onChange={(e) => setSearchParams({ ...searchParams, radius_km: parseInt(e.target.value) })}
              className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>5 km</span>
              <span>50 km</span>
              <span>100 km</span>
              <span>200 km</span>
            </div>
          </div>
        </div>

        {/* Search Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 md:py-4 rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-semibold text-base md:text-lg flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Searching...
            </>
          ) : (
            <>
              <Search size={20} />
              Search Lost & Found Pets
            </>
          )}
        </button>
      </form>

      {/* Location picker modal */}
      {showLocationPicker && (
        <LocationPicker
          onLocationSelected={handleLocationSelected}
          onClose={() => setShowLocationPicker(false)}
        />
      )}
    </>
  );
};

export default SearchFormComponent;